<template>
  <div class="comments">
    <div class="more-comments">
      <ion-button v-if="commentsData.countRemains > 0" mode="md" fill="clear" @click.stop="loadMore()">
        {{ $t('feed.comments.loadMore', remainingCount) }}
      </ion-button>

      <ion-button v-else-if="shouldShowViewAllButton" mode="md" fill="clear" @click.stop="showAll()">
        {{ $t('customPage.showAll') }}
      </ion-button>

      <ion-button v-else-if="shouldShowCollapseButton" mode="md" fill="clear" @click.stop="collapse()">
        {{ $t('hide') }}
      </ion-button>
    </div>

    <feed-comments-item
      v-for="(comment, i) in visibleComments"
      :key="`post_comment_${i}`"
      :post-comment-data="comment"
      :current-user-id="currentUserId"
      :post-id="postId"
      :feed-flag="feedFlag"
      :feed-type="feedType"
      @click.stop
      @on-comment-reply="handleCommentReply"
    />
  </div>
</template>

<script lang="ts" setup>
import { IonButton } from '@ionic/vue';
import type { ComputedRef, PropType } from 'vue';
import { computed, ref, watch } from 'vue';

import { FeedCommentsItem } from '@/components';
import type { FeedFilterTypeEnum, FeedFlagEnum } from '@/enums';
import { useToasts } from '@/helpers';
import { useI18n } from '@/i18n';
import { usePostStore } from '@/store';
import type { CommentsDataModel, PostCommentsModel } from '@/types';

const props = defineProps({
  commentsData: {
    type: Object as PropType<PostCommentsModel>,
    required: true,
  },
  postId: {
    type: Number,
    required: true,
  },
  currentUserId: {
    type: Number,
    required: true,
  },
  feedFlag: {
    type: String as PropType<FeedFlagEnum>,
    required: true,
  },
  feedType: {
    type: String as PropType<FeedFilterTypeEnum | null>,
    required: true,
  },
});

const emit = defineEmits(['onCommentReply']);

//#region Variables
const { t } = useI18n();
const { showSonnerToast } = useToasts();
const postStore = usePostStore();

const DEFAULT_VISIBILITY_THRESHOLD = 2;
const areAllVisible = ref(props.commentsData.count <= DEFAULT_VISIBILITY_THRESHOLD);
const visibleCount = ref(DEFAULT_VISIBILITY_THRESHOLD);

const commentsData: ComputedRef<PostCommentsModel> = computed(() => props.commentsData);
const visibleComments: ComputedRef<CommentsDataModel[]> = computed(() => {
  return areAllVisible.value ? commentsData.value.data : commentsData.value.data.slice(-visibleCount.value);
});
const remainingCount = computed(() => {
  const _visibleCount = areAllVisible.value ? commentsData.value.data.length : visibleCount.value;
  return props.commentsData.count - _visibleCount;
});

const hasMoreThanThreshold = computed(() => {
  return commentsData.value.count > DEFAULT_VISIBILITY_THRESHOLD && commentsData.value.countRemains === 0;
});
const shouldShowViewAllButton = computed(() => hasMoreThanThreshold.value && !areAllVisible.value);
const shouldShowCollapseButton = computed(() => hasMoreThanThreshold.value && areAllVisible.value);
//#endregion

//#region Methods
function handleCommentReply(comment: CommentsDataModel) {
  emit('onCommentReply', comment);
}

function collapse(): void {
  areAllVisible.value = false;
  visibleCount.value = DEFAULT_VISIBILITY_THRESHOLD;
}

async function loadMore(): Promise<void> {
  const data = { postId: props.postId, url: commentsData.value.loadMoreUrl };
  const success = await postStore.commentsLoadMore(data);

  if (!success) showSonnerToast(t('feed.comments.notLoad'), false);

  areAllVisible.value = true;
  visibleCount.value = DEFAULT_VISIBILITY_THRESHOLD;
}

async function showAll(): Promise<void> {
  areAllVisible.value = true;
  visibleCount.value = DEFAULT_VISIBILITY_THRESHOLD;
}
//#endregion

//#region Watchers
/**
 * @description Watches for changes in the number of comments and updates `visibleCommentsCount` accordingly.
 * - `visibleCommentsCount` will never be less than DEFAULT_VISIBILITY_THRESHOLD and more than comments count.
 * - If number of comments is even or less than DEFAULT_VISIBILITY_THRESHOLD, change show comments mode to true.
 */
watch(
  () => commentsData.value.count,
  (newLength, oldLength) => {
    const diff = newLength - oldLength;
    visibleCount.value = Math.max(
      DEFAULT_VISIBILITY_THRESHOLD,
      Math.min(commentsData.value.count, visibleCount.value + diff)
    );
    if (newLength <= DEFAULT_VISIBILITY_THRESHOLD) areAllVisible.value = true;
  }
);
//#endregion
</script>

<style scoped lang="scss">
.comments {
  border-top: 1px solid var(--ion-color-light-custom);

  &:hover {
    cursor: default;
  }

  .comments-no-padding {
    padding-top: 0;
  }
}
.more-comments {
  display: flex;
  justify-content: center;

  ion-button {
    --color: var(--ion-color-medium);
    font-size: 0.7em;
  }
}
</style>
